import type { ModelsResolver } from "@/models/resolvers";
import { resolveNestedList, useCacheOperations } from "@/composables/cache";
import { useLogger } from "@/composables/logger";
import { Order_By } from "gql/api";
import { revisionDefaultFields } from "@/models/resolvers";
import { perfEvalsWhereNotDeleted } from "./predicates";

const { info: logInfo } = useLogger("PERF_EVALS_RESOLVER");

export default {
  queries: {
    performance_evaluations: {
      xref_pumps: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "performance_evaluations",
          cache.keyOfField("xref_pumps", args) || "xref_pumps",
          "performance_evaluations_xref_pumps"
        );
      },
      author: (parent, args, cache, info) => {
        return parent.journal_author_id
          ? { __typename: "users", id: parent.journal_author_id }
          : null;
      },
      scenario(parent, args, cache, info) {
        return parent.scenario_id
          ? { __typename: "scenarios", id: parent.scenario_id }
          : null;
      }
    }
  },
  optimistic: {
    insert_revisions_performance_evaluations_revisions(vars, cache, info) {
      logInfo("Optimistic", { vars, cache, info });
      const affectedRows = vars.objects.length;

      return {
        __typename:
          "revisions_performance_evaluations_revisions_mutation_response",
        affected_rows: affectedRows,
        returning: vars.objects.map(object => ({
          id: object.id,
          __typename: "revisions_performance_evaluations_revisions",
          performance_evaluations_view: {
            pump_intake_pressure_psia: null,
            __typename: "performance_evaluations",
            author: null,
            ...object,
            ...revisionDefaultFields(object)
          }
        }))
      };
    },
    insert_revisions_performance_evaluations_xref_pumps_revisions(
      vars,
      cache,
      info
    ) {
      logInfo("Optimistic", { vars, cache, info });
      return {
        __typename:
          "revisions_performance_evaluations_xref_pumps_revisions_mutation_response",
        returning: vars.objects.map(object => ({
          id: object.id,
          __typename: "revisions_performance_evaluations_xref_pumps_revisions",
          performance_evaluations_xref_pumps_view: {
            __typename: "performance_evaluations_xref_pumps",
            ...object,
            ...revisionDefaultFields(object)
          }
        }))
      };
    }
  },
  updates: {
    insert_revisions_performance_evaluations_revisions(
      result,
      args,
      cache,
      info
    ) {
      logInfo("MUTATION UPDATE", { result, args, cache, info });

      args.objects.forEach(obj => {
        const { handleCacheUpdate } = useCacheOperations({
          cache,
          object: obj,
          typename: "performance_evaluations"
        });

        handleCacheUpdate(
          [
            {
              args: {
                order_by: { journal_created_at: Order_By.Asc },
                where: {
                  journal_deleted_at: { _is_null: true },
                  scenario: {
                    journal_deleted_at: { _is_null: true },
                    sizing: { journal_deleted_at: { _is_null: true } }
                  }
                }
              },
              relatedCacheKey: cache.keyOfEntity({
                __typename: "scenarios",
                id: obj.scenario_id
              }),
              relatedField: "performance_evaluations"
            }
          ],
          false
        );
      });
    },
    insert_revisions_performance_evaluations_xref_pumps_revisions(
      result,
      args,
      cache,
      info
    ) {
      logInfo("MUTATION UPDATE", { result, args, cache, info });

      args.objects.forEach(object => {
        const { handleCacheUpdate } = useCacheOperations({
          cache,
          object,
          typename: "performance_evaluations_xref_pumps"
        });

        handleCacheUpdate([
          {
            args: {
              where: {
                journal_deleted_at: {
                  _is_null: true
                }
              }
            },
            relatedCacheKey: cache.keyOfEntity({
              __typename: "performance_evaluations",
              id: object.performance_evaluation_id
            }),
            relatedField: "xref_pumps"
          }
        ]);
      });
    }
  }
} as ModelsResolver;
